import type { EncounterListItem } from "@elation/api-client/src/stitch-web/models/encounterListItem";
import { EncounterListItemCloseReason } from "@elation/api-client/src/stitch-web/models/encounterListItemCloseReason";
import type { ListEncountersByPatientOrder } from "@elation/api-client/src/stitch-web/models/listEncountersByPatientOrder";
import type { ListEncountersByPatientSortBy } from "@elation/api-client/src/stitch-web/models/listEncountersByPatientSortBy";
import { HeaderText } from "@src/components/general/HeaderText";
import { Text } from "@src/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/components/layout/Flexbox";
import { Badge } from "@src/components/ui/badge";
import { Button } from "@src/components/ui/button";
import { DataTable } from "@src/components/ui/data-table";
import { Skeleton } from "@src/components/ui/skeleton";
import { type ColumnDef } from "@tanstack/react-table";
import { AlertCircle, ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useMemo } from "react";

export type EncounterSortField = ListEncountersByPatientSortBy;
export type EncounterSortOrder = ListEncountersByPatientOrder;
export type { EncounterListItem };

export interface EncounterListProps {
  encounters: EncounterListItem[];
  isLoading?: boolean;
  isError?: boolean;

  sortBy?: EncounterSortField;
  order?: EncounterSortOrder;
  onSortChange?: (
    sortBy: EncounterSortField,
    order: EncounterSortOrder,
  ) => void;

  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  onNextPage?: () => void;
  onPrevPage?: () => void;

  onEncounterSelect?: (encounterId: number) => void;
}

function nextOrder(
  current: { sortBy: EncounterSortField; order: EncounterSortOrder },
  clicked: EncounterSortField,
): EncounterSortOrder {
  if (current.sortBy !== clicked) return "asc";
  return current.order === "asc" ? "desc" : "asc";
}

function SortIcon({ state }: { state: "asc" | "desc" | false }) {
  if (state === "asc") return <ArrowUp className="ml-1 size-3.5" />;
  if (state === "desc") return <ArrowDown className="ml-1 size-3.5" />;
  return <ArrowUpDown className="ml-1 size-3.5" />;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const CLOSE_REASON_LABELS: Record<EncounterListItemCloseReason, string> = {
  [EncounterListItemCloseReason.superseded_by_visit_note]: "Superseded",
  [EncounterListItemCloseReason.superseded_by_reopen]: "Superseded by reopen",
  [EncounterListItemCloseReason.abandoned]: "Abandoned",
  [EncounterListItemCloseReason.finalization_failed]: "Finalization failed",
  // "manual" = user-initiated close with no more-specific reason.
  [EncounterListItemCloseReason.manual]: "Closed",
};

function closedStatusLabel(reason: EncounterListItem["closeReason"]): string {
  return reason ? (CLOSE_REASON_LABELS[reason] ?? "Closed") : "Closed";
}

export const EncounterList = ({
  encounters,
  isLoading = false,
  isError = false,
  sortBy,
  order,
  onSortChange,
  hasNextPage = false,
  hasPrevPage = false,
  onNextPage,
  onPrevPage,
  onEncounterSelect,
}: EncounterListProps) => {
  const serverSortEnabled = Boolean(onSortChange && sortBy && order);

  const columns = useMemo<ColumnDef<EncounterListItem>[]>(() => {
    const serverSortHeader =
      (
        label: string,
        field: EncounterSortField,
      ): ColumnDef<EncounterListItem>["header"] =>
      () => {
        if (!serverSortEnabled || !onSortChange || !sortBy || !order) {
          return label;
        }
        const state = sortBy === field ? order : false;
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 px-2 text-muted-foreground"
            onClick={() =>
              onSortChange(field, nextOrder({ sortBy, order }, field))
            }
          >
            {label}
            <SortIcon state={state} />
          </Button>
        );
      };

    return [
      {
        accessorKey: "startedAt",
        enableSorting: false,
        header: serverSortHeader("Date", "startedAt"),
        cell: ({ row }) => {
          const isClosed = row.original.status === "closed";
          return (
            <HorizontalFlexbox alignItems="center" gap="xs">
              <Text
                fontWeight={500}
                appearance={isClosed ? "subdued" : undefined}
              >
                {formatDate(row.original.startedAt)}
              </Text>
              {isClosed && (
                <Badge variant="subtle">
                  {closedStatusLabel(row.original.closeReason)}
                </Badge>
              )}
            </HorizontalFlexbox>
          );
        },
      },
      {
        id: "physician",
        enableSorting: false,
        header: serverSortHeader("Physician", "physician"),
        accessorFn: (e) =>
          `${e.physicianFirstName} ${e.physicianLastName}`.trim(),
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {`${row.original.physicianFirstName} ${row.original.physicianLastName}`.trim()}
          </Text>
        ),
      },
      {
        accessorKey: "notePreview",
        enableSorting: false,
        header: "Note",
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {row.original.notePreview ?? "—"}
          </Text>
        ),
      },
    ];
  }, [onSortChange, sortBy, order, serverSortEnabled]);

  if (isLoading) {
    return (
      <VerticalFlexbox
        gap="sm"
        className="rounded-xl border border-border bg-card p-6 shadow-soft"
      >
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </VerticalFlexbox>
    );
  }

  if (isError) {
    return (
      <VerticalFlexbox
        alignItems="center"
        justifyContent="center"
        gap="xs"
        className="rounded-xl border border-destructive/50 bg-destructive/5 p-10 text-center"
      >
        <AlertCircle className="size-6 text-destructive" />
        <HeaderText tag="h3" fontWeight={600} className="text-base">
          Unable to load encounters
        </HeaderText>
        <Text sizing="sm" appearance="subdued" className="max-w-md">
          Something went wrong retrieving past encounters. Please try again in a
          moment.
        </Text>
      </VerticalFlexbox>
    );
  }

  if (encounters.length === 0) {
    return (
      <VerticalFlexbox
        alignItems="center"
        justifyContent="center"
        gap="xs"
        className="rounded-xl border border-dashed border-border bg-muted/40 p-10 text-center"
      >
        <HeaderText tag="h3" fontWeight={600} className="text-base">
          No past encounters for this patient yet.
        </HeaderText>
        <Text sizing="sm" appearance="subdued" className="max-w-md">
          Encounters will appear here once they're signed off or closed.
        </Text>
      </VerticalFlexbox>
    );
  }

  return (
    <VerticalFlexbox gap="sm">
      <DataTable<EncounterListItem, unknown>
        columns={columns}
        data={encounters}
        enablePagination={false}
        enableColumnToggle={false}
        onRowClick={
          onEncounterSelect
            ? (encounter) => onEncounterSelect(encounter.id)
            : undefined
        }
      />
      {(hasPrevPage || hasNextPage) && onNextPage && onPrevPage && (
        <HorizontalFlexbox
          alignItems="center"
          justifyContent="flex-end"
          gap="xs"
          className="py-1"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={onPrevPage}
            disabled={!hasPrevPage}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onNextPage}
            disabled={!hasNextPage}
          >
            Next
          </Button>
        </HorizontalFlexbox>
      )}
    </VerticalFlexbox>
  );
};
